import { useI18n } from '@ecom/i18n';
import { Badge, Button, Card } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import {
  problemMessage,
  showsProblem,
  STATE_LABEL_KEY,
  STATE_TEXT_KEY,
  STATE_TONE,
  testAvailability,
} from '../state';
import type { JibexAccountView } from '../types';

import { formatDateTime } from './format';

/**
 * « Statut du compte » — the one place the merchant reads whether Jibex is connected: a coloured badge, one sentence
 * in plain words, the reason when there is one, when it was last checked, and the test button.
 *
 * The badge is never the only carrier of the message (colour alone is not accessible): the label and the sentence say
 * the same thing in words, and the block is a polite live region so a screen reader announces a new verdict.
 */
export function JibexStatusCard({
  view,
  formIsDirty,
  testing,
  onTest,
  notice,
}: {
  readonly view: JibexAccountView;
  readonly formIsDirty: boolean;
  readonly testing: boolean;
  readonly onTest: () => void;
  /** A message about the last attempt that never reached the account (limit, outage) — already translated. */
  readonly notice: string | null;
}): ReactElement {
  const { t, locale } = useI18n();
  const availability = testAvailability(view, formIsDirty, testing);
  const problem = showsProblem(view.state) ? problemMessage(view.problemCode) : null;

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold text-ink">
          {t('merchant.delivery.account.status.title')}
        </h2>
        <Badge tone={STATE_TONE[view.state]}>{t(STATE_LABEL_KEY[view.state])}</Badge>
      </div>

      <div role="status" aria-live="polite" className="flex flex-col gap-2">
        <p className="text-sm text-ink">{t(STATE_TEXT_KEY[view.state])}</p>
        {problem !== null ? (
          <p className="text-sm text-inkSoft">{t(problem.key, problem.params)}</p>
        ) : null}
        {notice !== null ? (
          <p role="alert" className="rounded-md bg-warnSoft px-3 py-2 text-sm text-warn">
            {notice}
          </p>
        ) : null}
      </div>

      {view.account !== null ? (
        <dl className="grid gap-1 text-xs text-inkFaint sm:grid-cols-2">
          <div>
            <dt className="sr-only">{t('merchant.delivery.account.form.clientIdLabel')}</dt>
            <dd>
              {t('merchant.delivery.account.status.clientId', { clientId: view.account.clientId })}
            </dd>
          </div>
          <div>
            <dt className="sr-only">{t('merchant.delivery.account.history.colDate')}</dt>
            <dd>
              {view.lastCheckAt === null
                ? t('merchant.delivery.account.status.neverChecked')
                : t('merchant.delivery.account.status.lastCheck', {
                    date: formatDateTime(view.lastCheckAt, locale),
                  })}
            </dd>
          </div>
        </dl>
      ) : null}

      {!view.platformReady ? (
        <p className="rounded-md bg-infoSoft px-3 py-2 text-sm text-info">
          {t('merchant.delivery.account.platformNotReady')}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          onClick={onTest}
          loading={testing}
          disabled={!availability.allowed}
        >
          {testing
            ? t('merchant.delivery.account.test.running')
            : t('merchant.delivery.account.test.button')}
        </Button>
        {!availability.allowed && availability.reasonKey !== undefined && view.platformReady ? (
          <span className="text-xs text-inkFaint">{t(availability.reasonKey)}</span>
        ) : null}
      </div>
    </Card>
  );
}
